'use client' // eslint-disable-line string-to-lingui/missing-lingui-transformation

import { t } from '@lingui/macro'
import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react'
import { RouteComponentProps } from 'react-router-dom'
import { Thread } from '../components/thread'
import { Toaster } from '../components/ui/sonner'
import { useAuthenticatedClient } from '../providers/client'
import { StreamProvider } from '../providers/Stream'
import { ThreadProvider } from '../providers/Thread'
import '../styles/global.css'
import { SquarePen, History } from 'lucide-react'
import { StringParam, useQueryParam } from 'use-query-params'
import { ThreadHistoryModal } from '../components/ThreadHistoryModal'
import { Box, CircularProgress, Typography, FormControl, Select, MenuItem, InputLabel, IconButton, Alert, Tooltip } from '@mui/material'

const DEFAULT_API_URL = process.env.REACT_APP_FALCON_AGENT_API_URL as string

// Define interface for assistant data
interface Assistant {
    assistant_id: string
    name?: string
    [key: string]: any // Allow other properties
}

// Define component as React.FC with RouteComponentProps
const IngestView: React.FC<RouteComponentProps> = (props) => {
    // Use URL params if needed elsewhere, but no need to extract assistantNameFromURL here

    const [assistants, setAssistants] = useState<Assistant[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const client = useAuthenticatedClient(DEFAULT_API_URL)

    // Use a stable key for the StreamProvider to ensure it doesn't remount
    const stableProviderKey = useRef('stream-provider-key').current
    // Flag to track if we've initialized the assistant selection
    const initializedRef = useRef(false)

    // Use useQueryParam from use-query-params v2.x
    const [, setThreadId] = useQueryParam('threadId', StringParam)
    const [assistantId, setAssistantId] = useQueryParam('assistant', StringParam)

    // Get assistant name from ID - used for URL params
    const getAssistantNameById = useCallback(
        (id: string) => {
            const assistant = assistants.find((a) => a.assistant_id === id)
            // Never return the ID as fallback for URL display
            return assistant?.name || ''
        },
        [assistants]
    )

    // Main effect to load assistants only - no URL state management here
    useEffect(() => {
        let mounted = true

        if (!client) {
            return () => {
                mounted = false
            }
        }

        const loadAssistants = async () => {
            try {
                setLoading(true)
                const data = (await client.assistants.search({ limit: 100, offset: 0, metadata: {} })) as Assistant[]

                if (!mounted) return

                // First put sttm at the beginning if it exists, then sort the rest alphabetically
                let sortedAssistants = [...data]

                // Custom sort: sttm first, then alphabetical
                sortedAssistants.sort((a, b) => {
                    // Check if either is sttm
                    const aIsSttm = a.assistant_id?.toLowerCase() === 'sttm' || (a.name && a.name.toLowerCase() === 'sttm')
                    const bIsSttm = b.assistant_id?.toLowerCase() === 'sttm' || (b.name && b.name.toLowerCase() === 'sttm')

                    // If a is sttm, it should come first
                    if (aIsSttm && !bIsSttm) return -1
                    // If b is sttm, it should come first
                    if (!aIsSttm && bIsSttm) return 1

                    // Otherwise sort alphabetically
                    const nameA = (a.name || a.assistant_id || '').toLowerCase()
                    const nameB = (b.name || b.assistant_id || '').toLowerCase()
                    return nameA.localeCompare(nameB)
                })

                setAssistants(sortedAssistants)
                setLoading(false)

                // After loading, mark as initialized
                setTimeout(() => {
                    if (mounted) {
                        initializedRef.current = true
                    }
                }, 100)
            } catch (e: any) {
                if (mounted) {
                    setError(e.message)
                    setLoading(false)
                }
            }
        }

        loadAssistants()

        return () => {
            mounted = false
        }
    }, [client]) // Only depend on client

    // Separate effect for URL state initialization - runs once when assistants are loaded
    useEffect(() => {
        // Skip if no assistants loaded yet or already initialized
        if (!assistants.length || initializedRef.current) return

        // Find the assistant by URL name if provided
        let assistantToSelect: Assistant | undefined = undefined

        if (assistantId) {
            assistantToSelect = assistants.find(
                (a) => (a.name || '').toLowerCase() === assistantId.toLowerCase() || a.assistant_id?.toLowerCase() === assistantId.toLowerCase()
            )
        }

        // If no assistant found by URL, look for sttm
        if (!assistantToSelect) {
            assistantToSelect = assistants.find((a) => a.assistant_id?.toLowerCase() === 'sttm' || (a.name && a.name.toLowerCase() === 'sttm'))
        }

        // If still no assistant, use the first one
        if (!assistantToSelect && assistants.length > 0) {
            assistantToSelect = assistants[0]
        }

        // Only set URL if we found an assistant and URL param isn't already set
        if (assistantToSelect && !assistantId) {
            const assistantName = getAssistantNameById(assistantToSelect.assistant_id)
            // Use timeout to ensure this doesn't cause immediate render issues
            setTimeout(() => {
                setAssistantId(assistantName || undefined, 'pushIn')
            }, 0)
        }

        // Mark as initialized to prevent further runs
        initializedRef.current = true
    }, [assistants, assistantId, getAssistantNameById, setAssistantId])

    // Ref to store last valid assistant ID to prevent flashing
    const lastValidAssistantIdRef = useRef<string>('')

    // Find the selected assistant ID based on the URL param with a simpler approach
    const selectedAssistantId = useMemo(() => {
        if (!assistants.length) {
            return lastValidAssistantIdRef.current
        }

        let result = ''

        // If we have an assistant name in the URL, find the matching ID
        if (assistantId) {
            const foundAssistant = assistants.find(
                (a) => (a.name || '').toLowerCase() === assistantId.toLowerCase() || a.assistant_id?.toLowerCase() === assistantId.toLowerCase()
            )
            if (foundAssistant) result = foundAssistant.assistant_id
        }

        // If no match found, use default assistant (sttm) or first one
        if (!result) {
            const defaultAssistant =
                assistants.find((a) => a.assistant_id?.toLowerCase() === 'sttm' || (a.name && a.name.toLowerCase() === 'sttm')) || assistants[0]

            result = defaultAssistant?.assistant_id || ''
        }

        // Save the valid ID for future reference
        if (result) {
            lastValidAssistantIdRef.current = result
        }

        return result || lastValidAssistantIdRef.current
    }, [assistants, assistantId])

    // State for controlling the ThreadHistoryModal
    const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false)

    // If loading or error, show spinner or error
    if (loading) {
        return (
            <Box
                className='platform-assistant-ui'
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    width: '100%'
                }}
            >
                <CircularProgress size={24} color='primary' />
                <Typography variant='body1' sx={{ marginLeft: 1 }}>
                    {t`Loading assistants...`}
                </Typography>
            </Box>
        )
    }

    if (error) {
        return (
            <Alert severity='error' sx={{ m: 4 }}>
                {t`Error`}: {error}
            </Alert>
        )
    }

    if (!selectedAssistantId) {
        return (
            <Typography variant='body1' color='text.secondary' sx={{ p: 4 }}>
                {t`No assistants available.`}
            </Typography>
        )
    }

    return (
        <React.Suspense
            fallback={
                <Box
                    className='platform-assistant-ui'
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                        width: '100%'
                    }}
                >
                    <CircularProgress size={24} color='primary' />
                    <Typography variant='body1' sx={{ marginLeft: 1 }}>
                        {t`Loading...`}
                    </Typography>
                </Box>
            }
        >
            <Toaster />
            <ThreadProvider apiUrl={DEFAULT_API_URL}>
                <StreamProvider key={stableProviderKey} assistantId={selectedAssistantId} apiUrl={DEFAULT_API_URL}>
                    <Box
                        className='thread-component'
                        sx={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            position: 'relative',
                            flex: '1 1 auto',
                            bgcolor: 'var(--background)',
                            color: 'var(--foreground)',
                            margin: '0 auto',
                            overflow: 'auto'
                        }}
                    >
                        {/* Controls in top right */}
                        <Box
                            sx={{
                                position: 'absolute',
                                top: '1rem',
                                right: '1rem',
                                zIndex: 100,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1
                            }}
                        >
                            <FormControl size='small' variant='outlined' sx={{ minWidth: 180 }}>
                                <InputLabel id='assistant-select-label'>{t`Assistant`}</InputLabel>
                                <Select
                                    labelId='assistant-select-label'
                                    id='assistant-select'
                                    value={selectedAssistantId}
                                    label={t`Assistant`}
                                    onChange={(e) => {
                                        // Get the assistant name for display in URL
                                        const assistantName = getAssistantNameById(e.target.value as string)
                                        // Update URL directly
                                        setAssistantId(assistantName || undefined, 'pushIn')
                                    }}
                                >
                                    {assistants.map((a) => (
                                        <MenuItem key={a.assistant_id} value={a.assistant_id}>
                                            {a.name || a.assistant_id}
                                            {a.assistant_id === 'sttm' && (
                                                <Box component='span' sx={{ opacity: 0.7 }}>
                                                    {t` (default)`}
                                                </Box>
                                            )}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <Tooltip title={t`New conversation`}>
                                <IconButton onClick={() => setThreadId(undefined, 'pushIn')}>
                                    <SquarePen style={{ width: '20px', height: '20px', color: '#333333' }} />
                                </IconButton>
                            </Tooltip>

                            <Tooltip title={t`Chat history`}>
                                <IconButton onClick={() => setIsHistoryModalOpen((prev) => !prev)}>
                                    <History style={{ width: '20px', height: '20px', color: '#333333' }} />
                                </IconButton>
                            </Tooltip>
                        </Box>

                        <Thread />
                    </Box>
                </StreamProvider>
                <ThreadHistoryModal
                    isOpen={isHistoryModalOpen}
                    onOpenChange={(open) => {
                        setIsHistoryModalOpen(open)
                    }}
                    assistants={assistants}
                />
            </ThreadProvider>
        </React.Suspense>
    )
}

export default IngestView
